import type { PlaybackRegion } from '../PlaybackRegions';
import type { TextAlignment } from '../../enums';
import { PlaybackRegions } from '../PlaybackRegions';
import type { Transcript } from '../Transcript';
import { TextStyleRenderer } from './TextAsset/renderer/TextStyleRenderer';
import { TextMetrics } from './TextAsset/metrics/TextMetrics';
import type { CaptionAsset } from './CaptionAsset/asset/CaptionAsset';
import type { FancyCaptionAsset } from './FancyCaptionAsset/asset/FancyCaptionAsset';

/**
 * Converts milliseconds into a string representation for SRT.
 * Spec: https://www.w3.org/TR/webvtt1/#webvtt-timestamp
 */
export function convertToSRTTimestamp(milliseconds: number): string {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    milliseconds = Math.floor(milliseconds % 1000);
    seconds = seconds % 60;
    minutes = minutes % 60;
    return (
        hours.toString().padStart(2, '0') +
        ':' +
        minutes.toString().padStart(2, '0') +
        ':' +
        seconds.toString().padStart(2, '0') +
        ',' +
        milliseconds.toString().padStart(3, '0')
    );
}

function formatChunksAsSRT(
    timeOffset: number,
    playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
    captionAssetDuration: number,
    chunks: string[],
    chunkTimeRanges: number[][],
): string[] {
    return chunkTimeRanges.map((timeRange, index) => {
        const [startTime, endTime] = timeRange;
        const playbackStartTime = PlaybackRegions.assetTimeToPlaybackTime(
            playbackRegions,
            captionAssetDuration,
            startTime,
        );
        const playbackEndTime = PlaybackRegions.assetTimeToPlaybackTime(
            playbackRegions,
            captionAssetDuration,
            endTime,
        );

        const startString = convertToSRTTimestamp(playbackStartTime + timeOffset);
        const endString = convertToSRTTimestamp(playbackEndTime + timeOffset);
        return `${startString} --> ${endString}\n${chunks[index]}`;
    });
}

type MetricsBuilder = (settings: {
    transcript: Transcript;
    alignment: TextAlignment;
    style: TextStyleRenderer;
    maxWidth: number;
    maxHeight: number;
}) => TextMetrics[];

// TODO: REMOVE THIS COMMENT: This is only used by "share video".
/**
 * Returns each caption chunk as an SRT formatted string.
 */
export async function buildSRTFromCaptions(
    asset: CaptionAsset | FancyCaptionAsset,
    metricsBuilder: MetricsBuilder,
    isRendererReady: boolean,
    timeOffset: number,
    playbackRegions: Readonly<Readonly<PlaybackRegion>[]>,
    captionAssetDuration: number,
): Promise<string[]> {
    if (!asset.hasText) {
        return [];
    }

    let chunks: string[];
    let chunkTimeRanges: number[][];

    if (isRendererReady) {
        chunks = asset.renderer.getChunkTexts();
        chunkTimeRanges = asset.renderer.getChunkTimeRanges();
    } else {
        // The renderer metrics may not be built if it's not visible.
        await asset.onUsedFontsLoaded();
        const settings = asset.getRendererSettings();
        const style = new TextStyleRenderer();
        style.apply(settings.normalStyle);

        const metrics = metricsBuilder({
            transcript: asset.transcript,
            alignment: settings.alignment,
            style,
            maxWidth: settings.maxTextWidth,
            maxHeight: settings.maxTextHeight,
        });

        chunks = TextMetrics.getChunkTexts(metrics);
        chunkTimeRanges = TextMetrics.getChunkTimeRanges(metrics, asset.transcript);
    }

    const res = formatChunksAsSRT(
        timeOffset,
        playbackRegions,
        captionAssetDuration,
        chunks,
        chunkTimeRanges,
    );
    console.log(res);
    return res;
}
